import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { 
  CreditCard, TrendingUp, AlertTriangle, ShoppingCart, 
  Zap, Clock, Check, Loader2, ExternalLink
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserCreditsPanel = ({ user }) => {
  const [balance, setBalance] = useState(null);
  const [usage, setUsage] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);

  useEffect(() => {
    loadCreditsData();
  }, []);

  const loadCreditsData = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const [balanceRes, usageRes, packagesRes] = await Promise.all([
        axios.get(`${API}/credits/balance`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/credits/usage`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/credits/packages`)
      ]);
      
      setBalance(balanceRes.data);
      setUsage(usageRes.data);
      setPackages(packagesRes.data.packages || []);
    } catch (error) {
      console.error('Error loading credits:', error);
    } finally {
      setLoading(false);
    }
  };

  const purchaseCredits = async (packageId) => {
    setPurchasing(packageId);
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.post(`${API}/credits/purchase`, 
        { package_id: packageId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error iniciando compra');
    } finally {
      setPurchasing(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Balance Card */}
      <div className="bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm opacity-90 mb-1">Tus créditos disponibles</p>
            <p className="text-4xl font-bold">
              {balance?.unlimited ? '∞' : balance?.credits?.toLocaleString()}
            </p>
            {balance?.unlimited && (
              <span className="inline-block mt-2 px-3 py-1 bg-white/20 rounded-full text-sm">
                ✨ Créditos ilimitados
              </span>
            )}
          </div>
          <CreditCard className="w-16 h-16 opacity-30" />
        </div>
        
        {!balance?.unlimited && (
          <div className="mt-4 pt-4 border-t border-white/20">
            <div className="flex items-center justify-between text-sm">
              <span>Plan: {balance?.subscription_tier || 'Gratuito'}</span>
              <span>{balance?.monthly_credits_included?.toLocaleString()} créditos/mes</span>
            </div>
          </div>
        )}
      </div>

      {/* Alert if low credits */}
      {balance?.alert && (
        <div className={`rounded-xl p-4 flex items-start gap-3 ${
          balance.alert.type === 'critical' 
            ? 'bg-red-50 border border-red-200' 
            : 'bg-amber-50 border border-amber-200'
        }`}>
          <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${
            balance.alert.type === 'critical' ? 'text-red-500' : 'text-amber-500'
          }`} />
          <div>
            <p className={`font-medium ${
              balance.alert.type === 'critical' ? 'text-red-800' : 'text-amber-800'
            }`}>{balance.alert.message}</p>
            <p className="text-sm text-slate-600 mt-1">{balance.alert.description}</p>
          </div>
        </div>
      )}

      {/* Usage info */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5 text-amber-500" />
          ¿Qué consume créditos?
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {usage?.action_costs && Object.entries(usage.action_costs).map(([action, data]) => (
            <div key={action} className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">{data.description}</p>
              <p className="font-semibold text-indigo-600">{data.credits} créditos</p>
            </div>
          ))}
        </div>
      </div>

      {/* Purchase packages */}
      {!balance?.unlimited && packages.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-indigo-600" />
            Comprar más créditos
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {packages.map(pkg => (
              <div 
                key={pkg.id} 
                className={`relative p-4 rounded-xl border-2 transition-all ${
                  pkg.popular 
                    ? 'border-indigo-500 bg-indigo-50' 
                    : 'border-slate-200 hover:border-indigo-300'
                }`}
              >
                {pkg.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-indigo-500 text-white text-xs rounded-full">
                    Popular
                  </span>
                )}
                {pkg.discount && (
                  <span className="absolute -top-2 right-2 px-2 py-0.5 bg-green-500 text-white text-xs rounded-full">
                    -{pkg.discount}
                  </span>
                )}
                <p className="text-2xl font-bold text-slate-800">{pkg.credits?.toLocaleString()}</p>
                <p className="text-sm text-slate-500 mb-3">créditos</p>
                <p className="text-lg font-semibold text-indigo-600 mb-3">{pkg.price_eur}€</p>
                <Button 
                  className="w-full" 
                  variant={pkg.popular ? 'default' : 'outline'}
                  onClick={() => purchaseCredits(pkg.id)}
                  disabled={purchasing === pkg.id}
                >
                  {purchasing === pkg.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Comprar'
                  )}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent transactions */}
      {usage?.recent_transactions?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-slate-500" />
            Historial reciente
          </h3>
          <div className="space-y-2">
            {usage.recent_transactions.slice(0, 5).map((txn, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-slate-700">{txn.action}</p>
                  <p className="text-xs text-slate-500">
                    {txn.created_at ? new Date(txn.created_at).toLocaleString() : ''}
                  </p>
                </div>
                <span className={`font-medium ${
                  txn.amount > 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {txn.amount > 0 ? '+' : ''}{txn.amount}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserCreditsPanel;
